import { FV } from './share';
import { Button, Checkbox, Form, Input, InputNumber, Select } from 'antd';
import { useTranslate } from '@tolgee/react';
import { DisabledProps } from 'views/Aide/Scenario/TaskForm/share';
import { useFormikContext } from 'formik';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import styled from '@emotion/styled';
import { useActivityContext } from '../ActivityContext';
import { ReactNode, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { uniq } from 'lodash-es';
import useSmartField from 'hooks/useSmartField';
import { Render } from 'components/Render';
import { useMount } from 'ahooks';
import { useHelpContext } from '../HelpContext';
import { InlineHelp } from '../Help';
import Bricks from 'bricks.js';

const maxLength = 200;

function Highlight({ readonly, disabled }: { readonly?: boolean } & DisabledProps) {
  const { t } = useTranslate();

  const lang = useSystemLanguageValue();

  const { values, setValues } = useFormikContext<FV>();

  const { activityType, activityHighlight, highlightIds } = values;

  const {
    activityTypeHighlightListMap: { [activityType]: highlightList = [] },
  } = useActivityContext();

  const [editing, setEditing] = useState(Boolean(activityHighlight));

  const label =
    activityType === 'RECOMMEND_ACTIVITY' ? t('shangpinmaidian') : t('act_field_maidian');

  const {
    stepState: [, setHelpStep],
  } = useHelpContext();

  const containerRef = useRef<HTMLUListElement>(null);

  useLayoutEffect(() => {
    if (highlightList.length && containerRef.current) {
      Bricks({
        packed: 'data-packed',
        container: containerRef.current,
        sizes: [{ columns: 2, gutter: 12 }],
      }).pack();
    }
  }, [highlightList]);

  const withFormItem = (...nodes: ReactNode[]) => (
    <Form.Item label={label}>
      <div style={{ margin: '7px 0', fontSize: 12, color: '#84868c' }}>
        {t('小乐建议您选择三个以上的推荐卖点，以便于生成更吸引买家的活动内容')}
      </div>
      {...nodes}
    </Form.Item>
  );

  if (readonly) {
    return activityHighlight ? withFormItem(activityHighlight) : null;
  }

  const withRecommend = (...nodes: ReactNode[]) => {
    const content = (
      <Container>
        <span>{t('recommend_highlight')}</span>
        <ul ref={containerRef}>
          {highlightList.map(({ id, langMap }) => {
            const cnWord = langMap['zh-CN'];
            const word = langMap[lang] || langMap['en-US'] || cnWord;
            const checked = highlightIds?.includes(id);
            const onCheckedChange = (checked: boolean) => {
              setValues((values) => ({
                ...values,
                highlightIds: checked
                  ? uniq([...(highlightIds || []), id])
                  : highlightIds?.filter((_) => _ !== id),
              }));
              setHelpStep('活动卖点');
            };

            return (
              <li key={id}>
                <CheckContainer
                  style={checked ? { borderColor: 'var(--ant-primary-color)' } : undefined}
                >
                  <Checkbox
                    disabled={disabled}
                    checked={checked}
                    onChange={({ target: { checked } }) => onCheckedChange(checked)}
                  />
                  <Item disabled={disabled} template={word} cnTemplate={cnWord} />
                </CheckContainer>
              </li>
            );
          })}
        </ul>
        {...nodes}
      </Container>
    );

    return withFormItem(<InlineHelp step="活动卖点">{content}</InlineHelp>);
  };

  if (!editing) {
    return withRecommend(
      <div>
        <div />
        <Button
          disabled={disabled}
          type="text"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditing(true);
            setHelpStep('活动卖点');
          }}
        >
          {t('自定义活动卖点')}
        </Button>
      </div>,
    );
  }

  return withRecommend(
    <div>
      <span>{t('自定义活动卖点')}</span>
      <Button
        type="text"
        onClick={() => {
          setEditing(false);
          setValues((values) => ({ ...values, activityHighlight: undefined }));
          setHelpStep('活动卖点');
        }}
      >
        {t('取消')}
      </Button>
    </div>,
    <Input.TextArea
      disabled={disabled}
      placeholder={t('department.请输入x', { x: label })}
      maxLength={maxLength}
      showCount
      rows={4}
      value={activityHighlight}
      onChange={({ target: { value } }) =>
        setValues((values) => ({ ...values, activityHighlight: value }))
      }
      onFocus={() => setHelpStep('活动卖点')}
    />,
  );
}

let onParametersChange: undefined | ((parameters: FV['parameterIdToValue']) => void);
let waitUpdateParameters: FV['parameterIdToValue'];
const updateParameters = (parameters: FV['parameterIdToValue']) => {
  waitUpdateParameters = {
    ...waitUpdateParameters,
    ...parameters,
  };
  setTimeout(() => {
    if (waitUpdateParameters && onParametersChange) {
      onParametersChange(waitUpdateParameters);
      waitUpdateParameters = undefined;
    }
  });
};

function Item({
  disabled,
  template,
  cnTemplate,
}: {
  disabled?: boolean;
  template: string;
  cnTemplate: string;
}) {
  const {
    field: { value, onChange },
  } = useSmartField<FV['parameterIdToValue']>('parameterIdToValue');

  const [displayValue, setDisplayValue] = useState<any>();

  const {
    stepState: [, setHelpStep],
  } = useHelpContext();

  const result = useMemo(() => {
    try {
      const [, conceptJson] = template.match(/<concept>(.*)<\/concept>/) || [];
      const [, cnConceptJson] = cnTemplate.match(/<concept>(.*)<\/concept>/) || [];

      if (!conceptJson || !cnConceptJson) {
        return false;
      }

      const concept = JSON.parse(conceptJson) as Knowledge.Activity.HighlightConcept;
      const cnConcept = JSON.parse(cnConceptJson) as Knowledge.Activity.HighlightConcept;
      const [, pre = ''] = template.match(/^(.*)<concept>/) || [];
      const [, suf = ''] = template.match(/<\/concept>(.*)$/) || [];

      return {
        pre,
        suf,
        concept,
        cnConcept,
      };
    } catch (e) {
      return null;
    }
  }, [template, cnTemplate]);

  if (result === false) {
    return <div>{template}</div>;
  }

  if (!result) {
    return <></>;
  }

  const { pre, suf, concept, cnConcept } = result;
  const { defaultValue } = cnConcept;
  const { parameterId } = concept;
  const { [parameterId]: parameterValue } = value || {};

  const onParameterValueChange = (parameterValue: any) => {
    updateParameters({
      ...value,
      [parameterId]: !parameterValue && parameterValue !== 0 ? defaultValue : parameterValue,
    });
    setDisplayValue(parameterValue);
  };

  const render = (concept: ReactNode) => (
    <div>
      {pre}
      {concept}
      {suf}
      <Render>
        {() => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useMount(() => {
            onParametersChange = onChange;
            if (!parameterValue && parameterValue !== 0) {
              onParameterValueChange(defaultValue);
            } else {
              setDisplayValue(parameterValue);
            }
          });
          return null;
        }}
      </Render>
    </div>
  );

  if (concept.type === 'string') {
    return render(
      <Input
        disabled={disabled}
        placeholder={concept.defaultValue}
        value={displayValue}
        onChange={({ target: { value } }) => onParameterValueChange(value)}
        onFocus={() => setHelpStep('活动卖点')}
      />,
    );
  }

  if (concept.type === 'number') {
    return render(
      <InputNumber
        disabled={disabled}
        placeholder={String(concept.defaultValue)}
        value={displayValue}
        onChange={onParameterValueChange}
        onFocus={() => setHelpStep('活动卖点')}
      />,
    );
  }

  if (concept.type === 'enum') {
    if (cnConcept.type !== 'enum') {
      return <></>;
    }

    const options = cnConcept.enums.map((value, index) => ({
      value,
      label: concept.enums[index],
    }));

    return render(
      <Select
        disabled={disabled}
        dropdownMatchSelectWidth={false}
        placeholder={concept.defaultValue}
        value={displayValue}
        onChange={onParameterValueChange}
        options={options}
        onFocus={() => setHelpStep('活动卖点')}
      />,
    );
  }

  return <></>;
}

const Container = styled.div`
  max-width: 650px;
  padding: 16px;
  background: #fff;
  border: 1px solid #f0f2f5;
  border-radius: 4px;
  > span {
    font-size: 12px;
    color: #84868c;
  }
  > ul {
    width: 100%;
    margin: 12px 0;
    padding: 0;
    list-style: none;
    > li {
      width: calc(50% - 6px);
      display: inline-flex;
      justify-content: space-between;
      align-items: center;
      background: #fff;
      border-radius: 2px;
    }
  }
  > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 12px;
    border-top: 1px solid #f0f2f5;
    > button {
      padding-right: 0;
    }
  }
  > .ant-input-textarea {
    display: block;
    margin-bottom: 12px;
  }
`;

const CheckContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px;
  border: solid 1px #d3d7e0;
  border-radius: 2px;
  > label {
    flex: 0 0 auto;
    margin-right: 6px;
  }
  > div {
    flex: 1 0 0;
    .ant-input,
    .ant-select,
    .ant-input-number {
      display: inline-block;
      margin: 0 6px;
    }
    .ant-input {
      width: 100px;
    }
    .ant-select {
      width: auto;
    }
  }
`;

export default Highlight;
